/**
 * SupplierSkusCard — link supplier to real SKUs in the catalog.
 *
 * Replaces the free-text `suppliers.products[]` array with hard SKU links.
 * Typeahead picker (filters the SKU list as the operator types) + a chip
 * list of currently-linked SKUs.
 */
import { useState, useMemo, useEffect } from 'react';
import { Loader2, Plus, X, Tag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { trpc } from '@/lib/trpc';
import {
  useSupplierSkus,
  useSupplierEnrichmentMutations,
} from '../hooks/useSupplierEnrichment';

interface Props {
  supplierId: string;
  canEdit: boolean;
}

export function SupplierSkusCard({ supplierId, canEdit }: Props) {
  const linkedQuery = useSupplierSkus(supplierId);
  // Use the existing inventory.listSkus to fan the typeahead. Limited
  // staleTime keeps the picker reactive when SKUs are added elsewhere.
  const skusQuery = trpc.inventory.listSkus.useQuery({}, { staleTime: 60_000 });
  const { setSkus } = useSupplierEnrichmentMutations();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (linkedQuery.data) {
      setSelectedIds(new Set(linkedQuery.data.map((row) => row.sku_id)));
    }
  }, [linkedQuery.data]);

  const linkedRows = linkedQuery.data ?? [];
  const allSkus = useMemo(() => {
    const raw = (skusQuery.data ?? []) as Array<{ id: string; sku: string; title: string | null }>;
    // Normalize to the local shape the picker expects (sku_name maps from title).
    return raw.map((s) => ({ id: s.id, sku: s.sku, sku_name: s.title ?? null }));
  }, [skusQuery.data]);

  const PICKER_LIMIT = 50;
  const filteredCandidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allSkus.slice(0, PICKER_LIMIT);
    return allSkus
      .filter((s) => {
        if (selectedIds.has(s.id)) return false;
        return (
          s.sku?.toLowerCase().includes(term) ||
          (s.sku_name ?? '').toLowerCase().includes(term)
        );
      })
      .slice(0, PICKER_LIMIT);
  }, [allSkus, search, selectedIds]);

  /** True when the picker is showing exactly `PICKER_LIMIT` rows and the
   *  underlying catalog is bigger — i.e., results were silently truncated. */
  const isTruncated =
    filteredCandidates.length === PICKER_LIMIT
    && allSkus.length > PICKER_LIMIT
    && !search.trim();

  const commit = async (next: Set<string>) => {
    try {
      await setSkus.mutateAsync({ supplierId, skuIds: Array.from(next) });
    } catch (err) {
      toast({
        title: 'Failed to save SKU links',
        description: err instanceof Error ? err.message : 'Try again.',
        variant: 'destructive',
      });
      if (linkedQuery.data) {
        setSelectedIds(new Set(linkedQuery.data.map((row) => row.sku_id)));
      }
    }
  };

  const addSku = (id: string) => {
    if (!canEdit) return;
    const next = new Set(selectedIds);
    next.add(id);
    setSelectedIds(next);
    void commit(next);
  };

  const removeSku = (id: string) => {
    if (!canEdit) return;
    const next = new Set(selectedIds);
    next.delete(id);
    setSelectedIds(next);
    void commit(next);
  };

  const isLoading = linkedQuery.isLoading || skusQuery.isLoading;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Tag className="h-4 w-4" />
          Linked SKUs
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {linkedRows.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {linkedRows.map((row) => (
                  <span
                    key={row.id}
                    className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-1 text-xs font-medium"
                  >
                    <span className="font-mono">{row.sku ?? '-'}</span>
                    {row.sku_name && (
                      <span className="text-muted-foreground">· {row.sku_name}</span>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => removeSku(row.sku_id)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No SKUs linked. Use the search below to attach catalog SKUs.
              </p>
            )}

            {canEdit && (
              <div className="space-y-2 pt-2">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Search SKUs by code or name…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={() => setPickerOpen(true)}
                    className="h-8 text-xs"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setPickerOpen((v) => !v)}
                    className="h-8 px-3 text-xs"
                  >
                    {pickerOpen ? 'Hide' : <Plus className="h-3.5 w-3.5" />}
                  </Button>
                </div>
                {pickerOpen && (
                  <div className="max-h-48 overflow-auto rounded-md border bg-muted/20 p-1">
                    {isTruncated && (
                      <p className="px-2 py-1 text-[10px] text-muted-foreground italic">
                        Showing first {PICKER_LIMIT} of {allSkus.length} SKUs. Type to search the rest.
                      </p>
                    )}
                    {filteredCandidates.length === 0 ? (
                      <p className="px-2 py-1 text-xs text-muted-foreground">No matches.</p>
                    ) : (
                      filteredCandidates.map((sku) => (
                        <button
                          key={sku.id}
                          type="button"
                          onClick={() => addSku(sku.id)}
                          className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs hover:bg-background"
                          disabled={setSkus.isPending}
                        >
                          <span className="font-mono">{sku.sku}</span>
                          {sku.sku_name && (
                            <span className="text-muted-foreground truncate">{sku.sku_name}</span>
                          )}
                          <Plus className="h-3 w-3 shrink-0 text-muted-foreground" />
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
